/**
 * AdminCuration — pick which 3 campaigns appear on the public landing page.
 *
 * Left column: currently featured (3 slots, in order).
 * Right column: pool of eligible campaigns (any live campaign that isn't
 *   already featured).
 *
 * Actions: click a pool card to feature it (replaces the last-featured
 * campaign if 3 are already picked). Click X on a featured card to remove.
 */

import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, ArrowUp, ArrowDown } from "lucide-react";
import { SectionHeader } from "./_ui";
import { CAMPAIGN_ROSTER } from "../../data/adminMock";

const fmt$ = (n) => `$${Number(n).toLocaleString()}`;

export const AdminCuration = () => {
  const [order, setOrder] = useState([0, 1, 2]); // indices into CAMPAIGN_ROSTER

  const featured = order.map((i) => CAMPAIGN_ROSTER[i]);
  const pool = CAMPAIGN_ROSTER.filter((c) => !order.includes(c.idx) && c.daysLeft > 0);

  const add = (idx) => {
    if (order.length >= 3) {
      toast.error("3 slots max. Remove one before adding another.");
      return;
    }
    setOrder((prev) => [...prev, idx]);
    toast.success(`Added to homepage`);
  };
  const remove = (idx) => setOrder((prev) => prev.filter((i) => i !== idx));
  const move = (idx, dir) => setOrder((prev) => {
    const pos = prev.indexOf(idx);
    const next = [...prev];
    const swap = pos + dir;
    if (swap < 0 || swap >= next.length) return prev;
    [next[pos], next[swap]] = [next[swap], next[pos]];
    return next;
  });

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Homepage feed"
        title="Choose 3 campaigns to feature on the landing page."
        description="These are the students donors see first. Rotate weekly."
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Currently featured */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">Currently featured ({featured.length}/3)</p>
          <ul className="mt-3 space-y-3">
            {featured.map((c, pos) => (
              <li key={c.idx} className="rounded-lg border border-ink/10 bg-sand p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-serif text-lg text-ink">{c.name}</p>
                    <p className="mt-0.5 truncate text-xs text-ink/60">{c.school} · {c.category}</p>
                    <p className="mt-1 text-xs text-ink/70">{fmt$(c.raised)} of {fmt$(c.goal)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => move(c.idx, -1)}
                        disabled={pos === 0}
                        className="rounded-full p-1 text-ink/50 hover:bg-ink/5 hover:text-ink disabled:opacity-30"
                        data-testid={`move-up-${c.idx}`}
                      >
                        <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(c.idx, 1)}
                        disabled={pos === featured.length - 1}
                        className="rounded-full p-1 text-ink/50 hover:bg-ink/5 hover:text-ink disabled:opacity-30"
                        data-testid={`move-down-${c.idx}`}
                      >
                        <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(c.idx)}
                        className="rounded-full p-1 text-scarlet hover:bg-scarlet/10"
                        data-testid={`remove-${c.idx}`}
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </button>
                    </div>
                    <span className="text-[10px] uppercase tracking-wide text-ink/50">Slot {pos + 1}</span>
                  </div>
                </div>
              </li>
            ))}
            {featured.length === 0 && (
              <li className="rounded-lg border border-dashed border-ink/15 p-6 text-center text-sm text-ink/50">
                No campaigns featured. Pick from the pool.
              </li>
            )}
          </ul>
        </div>

        {/* Pool */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">Available pool ({pool.length})</p>
          <ul className="mt-3 space-y-2">
            {pool.map((c) => (
              <li key={c.idx} className="flex items-center justify-between rounded-lg border border-ink/10 bg-sand px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">{c.name}</p>
                  <p className="mt-0.5 truncate text-xs text-ink/60">{c.school} · {fmt$(c.raised)} of {fmt$(c.goal)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => add(c.idx)}
                  className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1 text-xs font-medium text-sand transition hover:bg-ink/90"
                  data-testid={`add-${c.idx}`}
                >
                  <Plus className="h-3 w-3" strokeWidth={2} /> Feature
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
